import { useEffect, useState } from "react";
import { httpsCallable } from "firebase/functions";
import { functions } from "../../firebase/config";
import { Field, inputClass, saveCmsDoc } from "../cmsApi.jsx";
import { CMS_MODULES, sanitizeModules } from "../../lib/cmsAccess";
import { PLATFORM_SYSTEMS, ALL_PLATFORM_SYSTEM_IDS } from "../../lib/platformSystems";
import {
  defaultCmsRoles,
  mergeCmsRoleCatalog,
  slugRoleId,
  isValidRoleId,
} from "../../lib/cmsRoles";

function mapCallableError(err) {
  const code = String(err?.code || "");
  const msg = String(err?.message || "No se pudo guardar.")
    .replace(/^FirebaseError:\s*/i, "")
    .replace(/^Firebase:\s*/i, "")
    .replace(/\s*\(.*\)\s*$/, "")
    .trim();
  if (
    code.includes("not-found") ||
    /not[- ]found|did not exist/i.test(msg) ||
    code.includes("internal") ||
    /^internal$/i.test(msg)
  ) {
    return "No se pudo guardar el catálogo de roles. Compruebe sesión OTP y permisos de owner.";
  }
  if (code.includes("permission-denied") || /permission/i.test(msg)) {
    return "Sin permiso para guardar Roles. Debe ser owner con sesión OTP vigente.";
  }
  if (code.includes("unauthenticated")) {
    return "Sesión expirada. Vuelva a iniciar sesión.";
  }
  return msg || "No se pudo guardar.";
}

function plainRoleCatalog(list) {
  return mergeCmsRoleCatalog(list).map((r) => ({
    id: String(r.id),
    label: String(r.label || r.id),
    builtin: Boolean(r.builtin),
    staffOtp: Boolean(r.staffOtp),
    canManageUsers: Boolean(r.canManageUsers),
    modules: Array.isArray(r.modules) ? r.modules.map((id) => String(id)) : [],
    systems: Array.isArray(r.systems) ? r.systems.map((id) => String(id)) : [],
  }));
}

export default function CmsRolesPanel({
  catalog,
  sourceCatalog,
  followSource = false,
  onSaved,
  onDraftsChange,
}) {
  const [drafts, setDrafts] = useState(() => mergeCmsRoleCatalog(catalog));
  const [userCounts, setUserCounts] = useState({});
  const [newId, setNewId] = useState("");
  const [newLabel, setNewLabel] = useState("");
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  const counts = userCounts || {};
  const pushDrafts = (next) => {
    setDrafts(next);
    onDraftsChange?.(next);
  };

  useEffect(() => {
    if (!followSource) return;
    setDrafts(mergeCmsRoleCatalog(sourceCatalog || catalog));
  }, [followSource, sourceCatalog, catalog]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const fn = httpsCallable(functions, "listCmsUsers");
        const result = await fn({});
        const next = {};
        for (const item of result.data?.items || []) {
          const id = String(item.role || "");
          next[id] = (next[id] || 0) + 1;
        }
        if (!cancelled) setUserCounts(next);
      } catch {
        if (!cancelled) setUserCounts({});
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const setRole = (id, patch) => {
    pushDrafts(drafts.map((r) => (r.id === id ? { ...r, ...patch } : r)));
  };

  const onAdd = () => {
    const id = slugRoleId(newId || newLabel);
    setError("");
    if (!isValidRoleId(id)) {
      setError("El id del rol debe empezar por letra y usar solo a-z, 0-9 y _.");
      return;
    }
    if (drafts.some((r) => r.id === id)) {
      setError("Ese rol ya existe.");
      return;
    }
    pushDrafts([
      ...drafts,
      {
        id,
        label: String(newLabel || id).trim() || id,
        builtin: false,
        staffOtp: true,
        canManageUsers: false,
        modules: ["dashboard"],
        systems: ["website"],
      },
    ]);
    setNewId("");
    setNewLabel("");
  };

  const onRemove = (role) => {
    if (role.builtin) return;
    const n = counts[role.id] || 0;
    if (n > 0) {
      setError(`No se puede quitar «${role.label}»: hay ${n} usuario(s) con ese rol.`);
      return;
    }
    pushDrafts(drafts.filter((r) => r.id !== role.id));
  };

  const onReset = () => {
    const next = mergeCmsRoleCatalog(sourceCatalog || catalog);
    setDrafts(next);
    onDraftsChange?.(null);
    setError("");
    setMessage("");
  };

  const onSave = async () => {
    setSaving(true);
    setError("");
    setMessage("");
    try {
      const roles = plainRoleCatalog(drafts);
      if (!roles.some((r) => r.id === "owner")) {
        setError("El catálogo debe incluir owner.");
        return;
      }
      for (const [roleId, n] of Object.entries(counts)) {
        if (n > 0 && !roles.some((r) => r.id === roleId)) {
          setError(`No se puede quitar el rol «${roleId}»: hay ${n} usuario(s) asignados.`);
          return;
        }
      }
      await saveCmsDoc("site", "cmsRoles", { roles });
      setDrafts(roles);
      setMessage("Catálogo de roles guardado.");
      onSaved?.(roles);
    } catch (err) {
      setError(mapCallableError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <p className="text-sm text-brand-dark/55">
        Las plantillas se aplican a todos los usuarios de ese rol. En Usuarios puede autorizar
        módulos extra a una cuenta. <strong>owner</strong> y <strong>medico</strong> están fijos
        (médico = solo Medicina). OTP staff = código de correo e inactividad de 10 min.
      </p>

      {error ? <p className="text-sm text-red-600">{error}</p> : null}
      {message ? <p className="text-sm text-brand-primary font-medium">{message}</p> : null}

      <div className="bg-white rounded-[28px] border border-brand-soft p-6 space-y-4">
        <h3 className="font-bold text-lg">Nuevo rol</h3>
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Nombre visible">
            <input
              className={inputClass}
              value={newLabel}
              onChange={(e) => setNewLabel(e.target.value)}
              placeholder="Auditor interno"
            />
          </Field>
          <Field label="Id técnico (opcional)">
            <input
              className={inputClass}
              value={newId}
              onChange={(e) => setNewId(e.target.value)}
              placeholder="auditor_interno"
            />
          </Field>
        </div>
        <button
          type="button"
          onClick={onAdd}
          className="text-xs font-bold uppercase tracking-widest text-brand-primary"
        >
          Agregar a la lista
        </button>
      </div>

      <ul className="space-y-4">
        {drafts.map((role) => {
          const locked = role.id === "owner";
          const medicoLocked = role.id === "medico";
          const n = counts[role.id] || 0;
          return (
            <li key={role.id} className="bg-white rounded-[28px] border border-brand-soft p-6 space-y-4">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <p className="font-bold text-brand-dark">{role.label}</p>
                  <p className="text-[10px] font-mono text-brand-dark/40">{role.id}</p>
                  <p className="text-[11px] text-brand-dark/45 mt-1">
                    {role.builtin ? "Rol de fábrica" : "Personalizado"} · {n} usuario(s)
                  </p>
                </div>
                {!role.builtin ? (
                  <button
                    type="button"
                    onClick={() => onRemove(role)}
                    className="text-[10px] font-bold uppercase tracking-widest text-red-600"
                  >
                    Quitar
                  </button>
                ) : null}
              </div>
              <Field label="Etiqueta">
                <input
                  className={inputClass}
                  disabled={locked}
                  value={role.label}
                  onChange={(e) => setRole(role.id, { label: e.target.value })}
                />
              </Field>
              <label className="flex items-center gap-2 text-sm text-brand-dark/80">
                <input
                  type="checkbox"
                  disabled={locked || medicoLocked}
                  checked={Boolean(role.staffOtp)}
                  onChange={(e) => setRole(role.id, { staffOtp: e.target.checked })}
                />
                OTP staff e inactividad 10 min
              </label>
              {medicoLocked ? (
                <p className="text-[11px] text-brand-dark/45">
                  Plantilla clínica: solo sistema Medicina. Las pantallas SIGMO las define Medicina
                  (laboratorio, expediente, documentos).
                </p>
              ) : null}
              <div className="space-y-2">
                <p className="text-[11px] font-bold uppercase tracking-widest text-brand-dark/55">
                  Módulos por defecto
                </p>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
                  {CMS_MODULES.map((m) => {
                    const lockUsers = m.id === "users" && role.id !== "admin" && role.id !== "owner";
                    const lockInformes =
                      m.id === "informes" && role.id !== "admin" && role.id !== "owner";
                    const boxLocked = locked || medicoLocked || lockUsers || lockInformes;
                    return (
                    <label
                      key={m.id}
                      className={`flex items-center gap-2 text-sm border border-brand-soft rounded-xl px-3 py-2 ${
                        boxLocked ? "opacity-60" : ""
                      }`}
                    >
                      <input
                        type="checkbox"
                        disabled={boxLocked}
                        checked={(role.modules || []).includes(m.id)}
                        onChange={(e) => {
                          const current = Array.isArray(role.modules) ? role.modules : [];
                          const next = e.target.checked
                            ? [...new Set([...current, m.id])]
                            : current.filter((id) => id !== m.id);
                          setRole(role.id, { modules: sanitizeModules(next) });
                        }}
                      />
                      {m.label}
                    </label>
                    );
                  })}
                </div>
              </div>
              <div className="space-y-2">
                <p className="text-[11px] font-bold uppercase tracking-widest text-brand-dark/55">
                  Sistemas por defecto
                </p>
                <div className="grid sm:grid-cols-3 gap-2">
                  {PLATFORM_SYSTEMS.map((s) => (
                    <label
                      key={s.id}
                      className={`flex items-center gap-2 text-sm border border-brand-soft rounded-xl px-3 py-2 ${
                        locked || medicoLocked ? "opacity-60" : ""
                      }`}
                    >
                      <input
                        type="checkbox"
                        disabled={locked || medicoLocked}
                        checked={(role.systems || []).includes(s.id)}
                        onChange={(e) => {
                          const current = Array.isArray(role.systems) ? role.systems : [];
                          let next = e.target.checked
                            ? [...new Set([...current, s.id])]
                            : current.filter((id) => id !== s.id);
                          if (!next.length) next = ["website"];
                          const allowed = new Set(ALL_PLATFORM_SYSTEM_IDS);
                          setRole(role.id, { systems: next.filter((id) => allowed.has(id)) });
                        }}
                      />
                      {s.label}
                    </label>
                  ))}
                </div>
              </div>
            </li>
          );
        })}
      </ul>

      <div className="flex flex-wrap gap-3">
        <button
          type="button"
          onClick={onSave}
          disabled={saving}
          className="bg-brand-primary text-white px-6 py-3 rounded-xl text-xs font-bold uppercase tracking-widest disabled:opacity-60"
        >
          {saving ? "Guardando…" : "Guardar roles"}
        </button>
        <button
          type="button"
          onClick={onReset}
          className="text-xs font-bold uppercase tracking-widest text-brand-dark/55"
        >
          Deshacer cambios
        </button>
      </div>
    </div>
  );
}
